import React, { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import Slider from "react-slick";
import "slick-carousel/slick/slick.css";
import "slick-carousel/slick/slick-theme.css";
import "./KeyServices.scss";

const ServiceCard = ({ title, description, details, icon, bgIcon, slug }) => {
  const [isHovered, setIsHovered] = useState(false);

  const cardContent = (
    <>
      <div className="bg-watermark">
        <img src={bgIcon} alt="" />
      </div>
      <div className="card-top">
        <div className="icon-circle">
          <img src={icon} alt={title} />
        </div>
      </div>
      <div className="card-body">
        <h5 className="card-title">{title}</h5>
        <p className="description">{description}</p>

        {details && details.length > 0 && (
          <div
            className="view-details-wrapper"
            onMouseEnter={() => setIsHovered(true)}
            onMouseLeave={() => setIsHovered(false)}
          >
            <span className="view-details">View Details</span>
            <AnimatePresence>
              {isHovered && (
                <motion.div
                  className="extra-details"
                  initial={{ height: 0, opacity: 0 }}
                  animate={{ height: "auto", opacity: 1 }}
                  exit={{ height: 0, opacity: 0 }}
                  transition={{ duration: 0.3, ease: "easeInOut" }}
                >
                  <ul>
                    {details.map((item, idx) => (
                      <li key={idx}>{item}</li>
                    ))}
                  </ul>
                </motion.div>
              )}
            </AnimatePresence>
          </div>
        )}
      </div>
    </>
  );

  if (slug) {
    return (
      <Link to={slug} className="service-card text-decoration-none">
        {cardContent}
      </Link>
    );
  }

  return <div className="service-card">{cardContent}</div>;
};

const KeyServices = () => {
  const [isMobile, setIsMobile] = useState(false);
  const [currentSlide, setCurrentSlide] = useState(0);
  const sliderRef = React.useRef(null);

  React.useEffect(() => {
    const handleResize = () => {
      setIsMobile(window.innerWidth <= 1024);
    };

    // Initial check
    handleResize();

    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  const servicesData = [
    {
      title: "Management Consulting",
      slug: "/services/management-consulting",
      description:
        "Strategic and operational advisory to improve performance and execution.",
      details: [
        "Enterprise Profitability Transformation ",
        "Cost Optimization Service ",
        "Go To Market Strategy ",
        "Monthly Review Meeting ",
        "Preparation of SOP",
      ],
      icon: "/icons/home/services/service-icn-1.svg",
      bgIcon: "/images/home/services/service-bg-1.webp",
    },
    {
      title: "Business Diagnostics",
      slug: "/services/business-diagnostic",
      description:
        "Rapid diagnostics to identify financial, operational, and strategic gaps.",
      icon: "/icons/home/services/service-icn-2.svg",
      bgIcon: "/images/home/services/service-bg-2.webp",
    },
    {
      title: "Business Transformation",
      slug: "/services/business-transformation",
      description:
        "End-to-end transformation to drive sustainable growth and execution discipline.",
      icon: "/icons/home/services/service-icn-3.svg",
      bgIcon: "/images/home/services/service-bg-3.webp",
    },
    {
      title: "CFO Services",
      slug: "/services/cfo-services",
      description: "On-demand CFO support for financial clarity and control.",
      icon: "/icons/home/services/service-icn-4.svg",
      bgIcon: "/images/home/services/service-bg-4.webp",
    },
    {
      title: "IPO Project Management",
      slug: "/services/ipo-project-management",
      description:
        "Structured project management for SME IPO execution and readiness.",
      icon: "/icons/home/services/service-icn-5.svg",
      bgIcon: "/images/home/services/service-bg-5.webp",
    },
    {
      title: "Detailed Project Report",
      slug: "project-feasibility-and-strategy-report",
      description:
        "Evaluation of project viability to support confident investment decisions.",
      icon: "/icons/home/services/service-icn-6.svg",
      bgIcon: "/images/home/services/service-bg-6.webp",
    },
    {
      title: "Startup Advisory",
      slug: "/services/startup-advisory",
      description:
        "Advisory support for startups across growth, capital, and scale-up stages.",
      icon: "/icons/home/services/service-icn-7.svg",
      bgIcon: "/images/home/services/service-bg-7.webp",
    },
    {
      title: "Fund Advisory",
      slug: "/services/fund-advisory",
      description:
        "We support fund houses in capital deployment with rigor, governance clarity, and long-term value protection.",
      details: [
        "Financial Due Diligence (FDD) ",
        "Valuation & Deal Structuring Support ",
        "Preparation of Investor Deck ",
        "Pre Deal Support ",
        "Post Deal Support ",
      ],
      icon: "/icons/home/services/service-icn-8.svg",
      bgIcon: "/images/home/services/service-bg-8.webp",
    },
    {
      title: "Transaction Advisory",
      slug: "/services/transaction-advisory",
      description:
        "Advisory across M&A, fundraising, and strategic transactions.",
      details: [
        "Mergers & Acquisitions ",
        "Fund Raising – Investment Banking ",
      ],
      icon: "/icons/home/services/service-icn-9.svg",
      bgIcon: "/images/home/services/service-bg-9.webp",
    },
    {
      title: "Human Capital",
      slug: "/services/human-capital",
      description:
        "People strategy advisory to drive performance and organisational growth.",
      details: [
        "KRA & KPI Design and Deployment ",
        "Performance Management System (PMS) ",
        "People Development & Growth Planning ",
        "Learning & Development (L&D) Frameworks ",
        "Employee Engagement Survey & Culture Building ",
      ],
      icon: "/icons/home/services/service-icn-10.svg",
      bgIcon: "/images/home/services/service-bg-10.webp",
    },
  ];

  const sliderSettings = {
    dots: false,
    infinite: true,
    speed: 500,
    slidesToShow: 1,
    slidesToScroll: 1,
    arrows: false,
    beforeChange: (current, next) => setCurrentSlide(next),
  };

  const nextSlide = () => {
    sliderRef.current.slickNext();
  };

  const prevSlide = () => {
    sliderRef.current.slickPrev();
  };

  return (
    <section className="key-services-section" id="key-services">
      <div className="container">
        <div className="header">
          <p className="subtitle">Key Services</p>
          <h2>Strategic & Financial Advisory Services</h2>
        </div>

        {isMobile ? (
          <div className="services-slider-wrapper">
            <Slider ref={sliderRef} {...sliderSettings}>
              {servicesData.map((service, index) => (
                <div key={index} className="slider-card-container">
                  <ServiceCard
                    title={service.title}
                    description={service.description}
                    details={service.details}
                    icon={service.icon}
                    bgIcon={service.bgIcon}
                    slug={service.slug}
                  />
                </div>
              ))}
            </Slider>
            <div className="slider-controls">
              <div className="counter">
                <span className="current">{currentSlide + 1}</span>
                <span className="total">/{servicesData.length}</span>
              </div>
              <div className="progress-bar-container">
                <div
                  className="progress-fill"
                  style={{
                    width: `${
                      ((currentSlide + 1) / servicesData.length) * 100
                    }%`,
                  }}
                ></div>
              </div>
              <div className="arrows">
                <button className="arrow-btn prev" onClick={prevSlide}>
                  <svg
                    width="24"
                    height="24"
                    viewBox="0 0 24 24"
                    fill="none"
                    xmlns="http://www.w3.org/2000/svg"
                  >
                    <path
                      d="M15 18L9 12L15 6"
                      stroke="currentColor"
                      strokeWidth="2"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    />
                  </svg>
                </button>
                <button className="arrow-btn next" onClick={nextSlide}>
                  <svg
                    width="24"
                    height="24"
                    viewBox="0 0 24 24"
                    fill="none"
                    xmlns="http://www.w3.org/2000/svg"
                  >
                    <path
                      d="M9 18L15 12L9 6"
                      stroke="currentColor"
                      strokeWidth="2"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    />
                  </svg>
                </button>
              </div>
            </div>
          </div>
        ) : (
          <div className="services-grid-wrapper">
            <div
              className="services-grid"
              style={{ "--items-count": servicesData.length }}
            >
              {servicesData.map((service, index) => (
                <ServiceCard
                  key={index}
                  title={service.title}
                  description={service.description}
                  details={service.details}
                  icon={service.icon}
                  bgIcon={service.bgIcon}
                  slug={service.slug}
                />
              ))}
            </div>
          </div>
        )}
      </div>
    </section>
  );
};

export default KeyServices;
